import React, { useEffect, useState } from 'react';
import { prefersReducedMotion } from './charts';
import './ModuleShowcase.css';
import {
  NqubeStage, AthenaStage, ListenStage, DevOpsStage, DevConsoleStage, ReviewCard,
} from './ModuleStages';
import AgentWeave from './AgentWeave';
import SwarmView from './SwarmView';
import { useSwarmReplay } from '../data/swarm';
import { LAUNCH_SWARM, LAUNCH_SWARM_META } from '../data/swarmDemo';

// Full-width showcase screens for /platform/:slug — animated recreations of
// the ACTUAL console module UIs (real block palette, real KPIs, real button
// labels and status values from frontend/src/pages/org/*), rendered with
// synthetic data. Bigger sibling of ModuleDemos.js; same rules: loops pause
// under prefers-reduced-motion, nothing here fabricates product claims.
// Reachout is the one exception — its console module doesn't exist yet, so
// its screen is labeled a console preview (see data/modules.js screenNote).

function useTick(ms, length) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const t = setInterval(() => setI((v) => (v + 1) % length), ms);
    return () => clearInterval(t);
  }, [ms, length]);
  return i;
}


/* ---- agentOS: manifest, gate, commit receipt -------------------------------- */

const OS_STAGES = [
  { label: 'Propose', detail: 'Model drafts INV-20841 · $12,480' },
  { label: 'Gate', detail: 'Amount > $10,000 · waiting on a person' },
  { label: 'Commit', detail: 'Runtime posts the webhook · secret stays vaulted' },
];

function AgentOsShowcase() {
  const tick = useTick(1800, OS_STAGES.length + 1);
  const active = Math.min(tick, OS_STAGES.length - 1);
  const committed = tick >= OS_STAGES.length;
  return (
    <div className="ms-os">
      <div className="ms-panel">
        <div className="ms-panel-title">Capability manifest</div>
        <div className="ms-svc"><span>webhook · https://erp.example/invoices</span><em className="ms-status done">declared</em></div>
        <div className="ms-svc"><span>dataset · ap-inbox</span><em className="ms-status done">declared</em></div>
        <div className="ms-svc"><span>admin.bypass</span><em className="ms-status error">not callable</em></div>
        <div className="ms-foot">The model never receives the signing secret.</div>
      </div>
      <div className="ms-panel ms-os-flow">
        <div className="ms-panel-title">This run</div>
        {OS_STAGES.map((s, i) => (
          <div key={s.label} className={`ms-os-stage ${i < active || committed ? 'done' : ''} ${i === active && !committed ? 'on' : ''}`}>
            <b>{i + 1}</b>
            <span>
              <strong>{s.label}</strong>
              {s.detail}
            </span>
          </div>
        ))}
      </div>
      <div className="ms-panel">
        <div className="ms-panel-title">Commit record</div>
        <div className={`ms-os-receipt ${committed ? 'in' : ''}`}>
          <div><span>actor</span><strong>runtime</strong></div>
          <div><span>capability</span><strong>webhook</strong></div>
          <div><span>review</span><strong>approved · $12,480</strong></div>
          <div><span>payload</span><strong>sha256 · a91c…</strong></div>
        </div>
        <div className="ms-foot">{committed ? 'Posted. The model did not hold the credential.' : 'Nothing is posted while the gate is open.'}</div>
      </div>
      <ReviewCard decided={committed} />
    </div>
  );
}

/* ---- Agent IO: a swarm coordinating live, then the specialists' backbone --- */

// The console's swarm picture, fed by the scripted replay of the seeded
// "Product launch" example (data/swarmDemo.js) — the same component that
// draws a real coordinator run in Agent IO from jobs/{id}/swarm.
function Swarm() {
  const doc = useSwarmReplay(LAUNCH_SWARM, { holdMs: 7000 });
  return (
    <div className="ms-swarm">
      <SwarmView doc={doc} title={LAUNCH_SWARM_META.label} subtitle={LAUNCH_SWARM_META.pipeline} tag="replay" compact />
      <div className="ms-foot">
        {LAUNCH_SWARM_META.note} The coordinator names roles from the library, runs them in parallel then in order,
        takes their suggestions under the cap, and judges each round. It cannot commit — the gate and the write live in the nQube pipeline.
      </div>
    </div>
  );
}

function Weave() {
  return (
    <div className="ms-panel">
      <div className="ms-panel-title">Specialists woven on the nQube backbone</div>
      <AgentWeave compact />
      <div className="ms-foot">
        The library lives here. One role embeds as nventr Agent. Several roles run together on an nQube coordinator. That block cannot commit.
      </div>
    </div>
  );
}

function AgentIoShowcase() {
  const tick = useTick(1400, 7);
  return (
    <div className="ms-stack">
      <Swarm />
      <div className="ms-cols">
      <div className="ms-panel">
        <div className="ms-panel-title">
          support-intake
          <span className="ms-btnrow">
            <span className="ms-status running">enabled</span>
            <span className="ms-btn">Rotate token</span>
          </span>
        </div>
        <div className="ms-field-label">System prompt</div>
        <div className="ms-textarea">
          Answer from projects and pipelines. You may propose a pipeline.
          You may not create a workload. You do not post to external systems.
        </div>
        <div className="ms-field-label">Can read</div>
        <div className="ms-featrow">
          <span className={`ms-toggle ${tick > 0 ? 'on' : ''}`}>project</span>
          <span className={`ms-toggle ${tick > 1 ? 'on' : ''}`}>pipeline</span>
          <span className="ms-toggle">workload</span>
        </div>
        <div className="ms-field-label">Can propose creating</div>
        <div className="ms-featrow">
          <span className={`ms-toggle ${tick > 2 ? 'on' : ''}`}>pipeline</span>
          <span className="ms-toggle">project</span>
          <span className="ms-toggle">workload</span>
        </div>
        <div className="ms-field-label">Look</div>
        <div className="ms-featrow">
          <span className="ms-toggle on">accent</span>
          <span className="ms-toggle on">right</span>
          <span className="ms-toggle">Chat</span>
        </div>
        <div className="ms-field-label">Install</div>
        <div className="ms-embed">
          script · embed.js
          <span className="ms-btn">copy</span>
        </div>
        <div className="ms-embed">
          iframe · /agent/frame
          <span className="ms-btn">copy</span>
        </div>
        <div className="ms-embed">
          API · session
          <span className="ms-btn">copy</span>
        </div>
      </div>
      <Weave />
      </div>
    </div>
  );
}

/* ---- nventr Agent: the embed on a page -------------------------------------- */

const NV_SCRIPT = [
  { who: 'you', text: 'Set up a pipeline for vendor invoices.' },
  { who: 'agent', text: 'I can propose that. I can’t post the invoice.' },
  { who: 'card', text: 'pipeline · invoice-intake' },
];

function NventrAgentShowcase() {
  const tick = useTick(1700, NV_SCRIPT.length + 1);
  const confirmed = tick >= NV_SCRIPT.length;
  return (
    <div className="ms-cols">
      <div className="ms-panel ms-site">
        <div className="ms-panel-title">intranet / help</div>
        <div className="ms-site-lines" aria-hidden="true">
          <i /><i /><i />
        </div>
        <div className="ms-widget">
          <div className="ms-widget-h">nventr Agent</div>
          {NV_SCRIPT.slice(0, Math.min(tick + 1, NV_SCRIPT.length)).map((line) => (
            <div key={line.text} className={`ms-wline ${line.who}`}>
              {line.who === 'card' ? (
                <>
                  <span>{line.text}</span>
                  <b>{confirmed ? '✓ confirmed' : 'Confirm'}</b>
                </>
              ) : line.text}
            </div>
          ))}
        </div>
      </div>
      <div className="ms-panel">
        <div className="ms-panel-title">After confirm</div>
        <div className={`ms-svc ${confirmed ? '' : 'dim'}`}>
          <span>pipeline · invoice-intake</span>
          <em className={`ms-status ${confirmed ? 'done' : 'launching'}`}>{confirmed ? 'created' : 'proposed'}</em>
        </div>
        <div className="ms-svc">
          <span>ERP post · INV-20841</span>
          <em className="ms-status error">not a tool</em>
        </div>
        <div className="ms-foot">
          Same token: script, iframe, or API. The record is empty until someone designs it in nQube. Posting the invoice is a workflow on agentOS.
        </div>
      </div>
    </div>
  );
}

/* ---- App Kit: the Apps module (AdminApps / AppsModule) ---------------------- */

const AK_STAGES = ['draft', 'provisioning', 'live'];

function AppKitShowcase() {
  const tick = useTick(2000, AK_STAGES.length + 1);
  const stage = AK_STAGES[Math.min(tick, 2)];
  return (
    <div className="ms-cols">
      <div className="ms-panel">
        <div className="ms-panel-title">New app</div>
        <div className="ms-field-label">Name</div>
        <div className="ms-textarea one">acme-portal</div>
        <div className="ms-field-label">Plan</div>
        <div className="ms-featrow">
          <span className="ms-toggle">Kit · $149/mo</span>
          <span className="ms-toggle on">Business · $349/mo</span>
        </div>
        <div className="ms-field-label">Deploy target</div>
        <div className="ms-featrow">
          <span className="ms-toggle on">managed</span>
          <span className="ms-toggle">AWS</span>
          <span className="ms-toggle">Azure</span>
          <span className="ms-toggle">GCP</span>
          <span className="ms-toggle">on-prem</span>
        </div>
        <div className="ms-btnrow" style={{ marginTop: 12 }}>
          <span className="ms-btn primary">Create app</span>
        </div>
      </div>
      <div className="ms-panel">
        <div className="ms-panel-title">
          acme-portal
          <span className={`ms-status ${stage === 'live' ? 'running' : 'launching'}`}>{stage}</span>
        </div>
        <div className="ms-dim" style={{ marginBottom: 10 }}>acme.apps.nventr.io</div>
        <div className={`ms-btnrow ${stage === 'live' ? '' : 'dim'}`}>
          <span className="ms-btn primary">Open app</span>
          <span className="ms-btn">Open Dev Console</span>
        </div>
        <div className="ms-foot">
          Your URL, your database, your own code layer in its own repo —
          the platform modules ride along.
        </div>
      </div>
    </div>
  );
}

/* ---- Reachout: console preview (no console module yet) ---------------------- */

const RO_RULES = [
  ['gate held > $10k', 'email approvals@acme.com'],
  ['run failed', 'slack #ops'],
  ['invoice posted', 'webhook → erp'],
  ['error rate > 2%', 'sms on-call'],
];

const RO_DELIVERIES = [
  ['12:41', 'email approvals@', 'gate held · INV-20841', 'delivered'],
  ['12:39', 'webhook erp', 'payload · 214 rows', 'delivered'],
  ['12:36', 'slack #ops', 'error rate 2.1%', 'delivered'],
  ['12:31', 'sms on-call', 'gate pending > $10k', 'queued'],
];

function ReachoutShowcase() {
  const tick = useTick(1800, RO_DELIVERIES.length);
  return (
    <div className="ms-cols">
      <div className="ms-panel">
        <div className="ms-panel-title">Rules · trigger → destination</div>
        {RO_RULES.map(([trigger, dest]) => (
          <div key={trigger} className="ms-svc">
            <span>{trigger}</span>
            <em className="ms-dim">→ {dest}</em>
          </div>
        ))}
      </div>
      <div className="ms-panel">
        <div className="ms-panel-title">Deliveries</div>
        {RO_DELIVERIES.map(([at, dest, what, st], i) => (
          <div key={what} className={`ms-svc ${i === tick ? 'sel' : ''}`}>
            <span className="ms-dim">{at}</span>
            <span>{dest} · {what}</span>
            <em className={`ms-status ${st === 'delivered' ? 'done' : 'launching'}`}>
              {st === 'delivered' ? '✓ delivered' : '⏸ queued'}
            </em>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---- Registry + frame -------------------------------------------------------- */

const SHOWCASES = {
  nqube: { Screen: NqubeStage, host: 'nQube · ex-launch-swarm' },
  athena: { Screen: AthenaStage, host: 'Athena · Insights' },
  'dev-console': { Screen: DevConsoleStage, host: 'instance /dev' },
  listen: { Screen: ListenStage, host: 'Listen · ap-inbox' },
  devops: { Screen: DevOpsStage, host: 'DevOps · deploys & workloads' },
  agentos: { Screen: AgentOsShowcase, host: 'nQube run · agentOS' },
  'agent-io': { Screen: AgentIoShowcase, host: 'app.nventr.io/orgs/acme/agents' },
  'nventr-agent': { Screen: NventrAgentShowcase, host: 'your-site · embed' },
  'app-kit': { Screen: AppKitShowcase, host: 'app.nventr.io/orgs/acme/apps' },
  reachout: { Screen: ReachoutShowcase, host: 'reachout · console preview' },
};

export default function ModuleShowcase({ slug }) {
  const entry = SHOWCASES[slug];
  if (!entry) return null;
  const { Screen, host } = entry;
  return (
    <div className="ms-frame">
      <div className="ms-bar">
        <i /><i /><i />
        <span>{host}</span>
      </div>
      <div className="ms-body">
        <Screen />
      </div>
    </div>
  );
}
